// Scheduled publish controls, shared by the Archive list cards and the
// article page's Membership panel. Only offered on an unpublished
// ('excluded') article; the worker publishes it within five minutes of the
// chosen Eastern time, with the same refusals as the Publish button.

import { useEffect, useState } from 'react'
import {
  easternInputValue, fmtEastern, scheduleError, useScheduleArticle,
} from './usePublishArticle'

const BTN = 'font-mono tracking-label uppercase disabled:opacity-50'

export function ScheduleControl({
  id, publishAt, lastError, compact = false,
}: {
  id: number
  publishAt: string | null | undefined
  lastError?: string | null
  compact?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(() => easternInputValue(publishAt))
  const save = useScheduleArticle(id)
  useEffect(() => { if (save.isSuccess) setEditing(false) }, [save.isSuccess])
  const err = scheduleError(save.error)
  const size = compact ? 'text-[10px]' : 'text-[11px]'

  if (editing) {
    return (
      <div className={`flex flex-col gap-1.5 ${compact ? 'items-end' : ''}`}>
        <label className={`font-mono ${size} text-paper-trace`}>
          Publish at (Eastern time)
        </label>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={`bg-paper-deepest border border-paper-trace px-2 py-1 font-mono ${size} text-paper`}
        />
        {err && <span className={`font-mono ${size} text-radar-red`}>{err}</span>}
        <div className="flex gap-2">
          <button
            onClick={() => save.mutate(value)}
            disabled={save.isPending || !value}
            className={`${BTN} ${size} border border-amber-glow/60 text-amber-glow px-2 py-0.5 hover:bg-amber-glow/10`}
          >
            {save.isPending ? 'Saving…' : 'Save schedule'}
          </button>
          <button
            onClick={() => { setEditing(false); save.reset() }}
            disabled={save.isPending}
            className={`${BTN} ${size} text-paper-trace hover:text-paper`}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if (publishAt) {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${compact ? 'justify-end' : ''}`}>
        <span className={`font-mono ${size} text-amber-glow`}>
          Scheduled · {fmtEastern(publishAt)}
        </span>
        <button
          onClick={() => { setValue(easternInputValue(publishAt)); setEditing(true) }}
          className={`${BTN} ${size} text-paper-trace hover:text-paper`}
        >
          Change
        </button>
        <button
          onClick={() => save.mutate(null)}
          disabled={save.isPending}
          className={`${BTN} ${size} text-paper-trace hover:text-radar-red`}
        >
          {save.isPending ? 'Working…' : 'Cancel schedule'}
        </button>
        {err && <span className={`font-mono ${size} text-radar-red`}>{err}</span>}
      </div>
    )
  }

  return (
    <div className={`flex flex-col gap-1 ${compact ? 'items-end' : ''}`}>
      {lastError && <span className={`font-mono ${size} text-radar-red`}>{lastError}</span>}
      <button
        onClick={() => { setValue(easternInputValue(null)); setEditing(true) }}
        className={`${BTN} ${size} border border-amber-glow/50 text-amber-glow px-2 py-0.5 hover:bg-amber-glow/10`}
      >
        Schedule…
      </button>
    </div>
  )
}
